import { useCallback, useMemo } from "react";
import { useAuth } from "./useAuth";

export function usePermission() {
  const { user } = useAuth();

  const userRoles = useMemo(() => {
    return (user?.roles || []).map((r) => r.toUpperCase());
  }, [user]);

  const userPermissions = useMemo(() => {
    return (user?.permissions || []).map((p) => p.toLowerCase());
  }, [user]);

  const isAdmin = useMemo(() => {
    return userRoles.includes("ADMIN") || userPermissions.includes("*");
  }, [userRoles, userPermissions]);

  /**
   * Verifica se o usuário autenticado possui a permissão especificada.
   * Suporta wildcards como "applications:*" e override de ADMIN.
   */
  const hasPermission = useCallback(
    (permission: string): boolean => {
      if (isAdmin) return true;
      const target = permission.toLowerCase();

      return userPermissions.some((perm) => {
        if (perm === target) return true;
        if (perm.endsWith(":*")) {
          const prefix = perm.slice(0, -2);
          return target.startsWith(`${prefix}:`);
        }
        return false;
      });
    },
    [isAdmin, userPermissions],
  );

  /**
   * Verifica se o usuário autenticado possui a role especificada (case-insensitive).
   */
  const hasRole = useCallback(
    (role: string): boolean => {
      return userRoles.includes(role.toUpperCase());
    },
    [userRoles],
  );

  /**
   * Verifica se o usuário atende aos requisitos de permissão ou role.
   */
  const canAccess = useCallback(
    (requiredPermissions?: string[], requiredRoles?: string[]): boolean => {
      if (isAdmin) return true;

      if (requiredRoles && requiredRoles.length > 0) {
        const matchRole = requiredRoles.some((r) => hasRole(r));
        if (!matchRole) return false;
      }

      if (requiredPermissions && requiredPermissions.length > 0) {
        const matchPerm = requiredPermissions.every((p) => hasPermission(p));
        if (!matchPerm) return false;
      }

      return true;
    },
    [hasPermission, hasRole, isAdmin],
  );

  return {
    hasPermission,
    hasRole,
    canAccess,
    isAdmin,
    userRoles,
    userPermissions,
  };
}
